import React from 'react';
import styled from 'styled-components';
import { SKILLS_CATEGORIES } from '../../config/skillsCategories';

const ChartContainer = styled.div`
  width: 100%;
  height: 300px;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ChartSvg = styled.svg`
  width: 100%;
  height: 100%;
  max-width: 300px;
  max-height: 300px;
`;

const RadarGrid = styled.polygon`
  fill: none;
  stroke: var(--border-color);
  stroke-width: 1;
  opacity: 0.3;
`;

const RadarAxis = styled.line`
  stroke: var(--border-color);
  stroke-width: 1;
  opacity: 0.5;
`;

const RadarArea = styled.polygon<{ color: string }>`
  fill: ${props => props.color};
  fill-opacity: 0.2;
  stroke: ${props => props.color};
  stroke-width: 2;
  stroke-linejoin: round;
`;

const RadarPoint = styled.circle<{ color: string }>`
  fill: ${props => props.color};
  stroke: white;
  stroke-width: 2;
  cursor: pointer;
  transition: r 0.2s ease;
  
  &:hover {
    r: 6;
  }
`;

const RadarLabel = styled.text<{ color?: string }>`
  font-size: 12px;
  fill: ${props => props.color || 'var(--text-primary)'};
  text-anchor: middle;
  dominant-baseline: middle;
  font-weight: 600;
`;

const ScaleLabel = styled.text`
  font-size: 10px;
  fill: var(--text-secondary);
  text-anchor: middle;
  dominant-baseline: middle;
`;

const Tooltip = styled.div<{ x: number; y: number; visible: boolean }>`
  position: absolute;
  left: ${props => props.x}px;
  top: ${props => props.y}px;
  background: var(--card-background);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-sm);
  padding: var(--spacing-sm);
  font-size: 0.75rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  pointer-events: none;
  z-index: 10;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.2s ease;
  transform: translate(-50%, -100%);
  white-space: nowrap;
  
  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: var(--card-background);
  }
`;

interface RadarDataPoint {
  label: string;
  value: number;
  color: string;
}

interface RadarChartProps {
  data: RadarDataPoint[];
  size?: number;
  maxValue?: number;
  levels?: number;
}

export const RadarChart: React.FC<RadarChartProps> = ({
  data,
  size = 300,
  maxValue = 10,
  levels = 5
}) => {
  const [tooltip, setTooltip] = React.useState<{
    visible: boolean;
    x: number;
    y: number;
    content: string;
  }>({ visible: false, x: 0, y: 0, content: '' });

  const center = size / 2;
  const radius = (size / 2) - 60; // Leave space for labels
  const angleStep = (2 * Math.PI) / data.length;

  // Calculate points for each data value
  const getPoint = (index: number, value: number) => {
    const angle = (index * angleStep) - (Math.PI / 2); // Start from top
    const r = (value / maxValue) * radius;
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };

  // Calculate label positions
  const getLabelPoint = (index: number) => {
    const angle = (index * angleStep) - (Math.PI / 2);
    const r = radius + 30; // Outside the chart
    return {
      x: center + r * Math.cos(angle),
      y: center + r * Math.sin(angle)
    };
  };

  // Generate grid polygons
  const generateGridPolygon = (level: number) => {
    const points = [];
    for (let i = 0; i < data.length; i++) {
      const point = getPoint(i, (level / levels) * maxValue);
      points.push(`${point.x},${point.y}`);
    }
    return points.join(' ');
  };

  // Generate data polygon
  const generateDataPolygon = () => {
    const points = data.map((item, index) => {
      const point = getPoint(index, item.value);
      return `${point.x},${point.y}`;
    });
    return points.join(' ');
  };

  const handlePointHover = (event: React.MouseEvent, dataPoint: RadarDataPoint, index: number) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const containerRect = event.currentTarget.closest('div')?.getBoundingClientRect();
    
    if (containerRect) {
      setTooltip({
        visible: true,
        x: rect.left - containerRect.left + rect.width / 2,
        y: rect.top - containerRect.top,
        content: `${dataPoint.label}: ${dataPoint.value.toFixed(1)}/10`
      });
    }
  };

  const handlePointLeave = () => {
    setTooltip(prev => ({ ...prev, visible: false }));
  };

  if (data.length === 0) {
    return (
      <ChartContainer>
        <div style={{ 
          color: 'var(--text-secondary)',
          fontSize: '0.875rem'
        }}>
          No data available for radar chart
        </div>
      </ChartContainer>
    );
  }

  // Use the first data point's color for the entire chart
  const chartColor = data[0]?.color || SKILLS_CATEGORIES.technical.color;

  return (
    <ChartContainer>
      <ChartSvg viewBox={`0 0 ${size} ${size}`}>
        {/* Grid levels */}
        {Array.from({ length: levels }, (_, i) => (
          <RadarGrid
            key={`grid-${i}`}
            points={generateGridPolygon(i + 1)}
          />
        ))}
        
        {/* Axes */}
        {data.map((_, index) => {
          const endPoint = getPoint(index, maxValue);
          return (
            <RadarAxis
              key={`axis-${index}`}
              x1={center}
              y1={center}
              x2={endPoint.x}
              y2={endPoint.y}
            />
          );
        })}
        
        {/* Scale labels */}
        {Array.from({ length: levels }, (_, i) => {
          const value = ((i + 1) / levels) * maxValue;
          const point = getPoint(0, value);
          return (
            <ScaleLabel
              key={`scale-${i}`}
              x={point.x + 10}
              y={point.y}
            >
              {value.toFixed(1)}
            </ScaleLabel>
          );
        })}
        
        {/* Data area */}
        <RadarArea
          color={chartColor}
          points={generateDataPolygon()}
        />
        
        {/* Data points */}
        {data.map((dataPoint, index) => {
          const point = getPoint(index, dataPoint.value);
          return (
            <RadarPoint
              key={`point-${index}`}
              color={chartColor}
              cx={point.x}
              cy={point.y}
              r={4}
              onMouseEnter={(e) => handlePointHover(e, dataPoint, index)}
              onMouseLeave={handlePointLeave}
            />
          );
        })}
        
        {/* Labels */}
        {data.map((dataPoint, index) => {
          const labelPoint = getLabelPoint(index);
          return (
            <RadarLabel
              key={`label-${index}`}
              x={labelPoint.x}
              y={labelPoint.y}
              color={dataPoint.color}
            >
              {dataPoint.label}
            </RadarLabel>
          );
        })}
      </ChartSvg>
      
      <Tooltip
        x={tooltip.x}
        y={tooltip.y}
        visible={tooltip.visible}
      >
        {tooltip.content}
      </Tooltip>
    </ChartContainer>
  );
};